// frontend/src/components/ImpactAnalyzer.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';
import { FaExclamationTriangle, FaCheckCircle, FaTasks, FaLightbulb, FaClock, FaShieldAlt } from 'react-icons/fa';
import Spinner from './spinner';
import './impact-analyzer-improvements.css';

function ImpactAnalyzer({ nodes = [], onNodeSelect = () => {} }) {
  const { t } = useTranslation();
  const [selectedNode, setSelectedNode] = useState('');
  const [changeType, setChangeType] = useState('update');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [impactedNodes, setImpactedNodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Load analytics on mount
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const response = await apiClient.get('/impact-analytics');
        setAnalytics(response.data);
      } catch (error) {
        console.error('Error loading analytics:', error);
      }
    };
    loadAnalytics();
  }, []);
  
  const handleAnalyze = async () => {
    if (!selectedNode) return;
    
    setLoading(true);
    
    try {
      const response = await apiClient.post('/impact-analyze', {
        change: {
          type: "node",
          action: changeType,
          name: selectedNode
        }
      });
      
      setResults(response.data);
      setImpactedNodes(response.data.impactedNodes || []);
      
      // Reload analytics after analysis
      const analyticsResponse = await apiClient.get('/impact-analytics');
      setAnalytics(analyticsResponse.data);
    } catch (error) {
      alert(t('impactAnalyzer.analysisError', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };
  
  const ConfidenceIndicator = ({ score }) => (
    <div className="confidence-indicator">
      <span className="confidence-label">{t('impactAnalyzer.confidenceLabel')}:</span>
      <div className="confidence-bar">
        <div 
          className={`confidence-fill ${score > 70 ? 'high' : score > 40 ? 'medium' : 'low'}`}
          style={{ width: `${score}%` }}
        ></div>
      </div>
      <span className="confidence-value">{score}%</span>
    </div>
  );
  
  const ContextualActions = ({ actions = [] }) => (
    <div className="contextual-actions">
      <h3>{t('impactAnalyzer.recommendedActions')}</h3>
      {actions.map((action, i) => (
        <div key={i} className={`action-card ${action.priority}`}>
          <div className="action-header">
            <span className="action-title">{action.title}</span>
            <span className="action-urgency">{action.urgency}</span>
          </div>
          <p className="action-description">{action.description}</p>
          <div className="action-details">
            <span className="action-effort">⏱️ {action.effort}</span>
            <span className="action-team">👥 {action.responsibleTeam}</span>
          </div>
          <div className="action-criteria">
            <strong>{t('impactAnalyzer.successCriteria')}:</strong> {action.successCriteria}
          </div>
        </div>
      ))}
    </div>
  );
  
  const GraphMetrics = ({ metrics }) => (
    <div className="graph-metrics">
      <h4>{t('impactAnalyzer.graphMetrics')}</h4>
      <div className="metrics-grid">
        <div className="metric">
          <span className="metric-label">{t('impactAnalyzer.metrics.nodes')}:</span>
          <span className="metric-value">{metrics.totalNodes}</span>
        </div>
        <div className="metric">
          <span className="metric-label">{t('impactAnalyzer.metrics.relations')}:</span>
          <span className="metric-value">{metrics.totalRelations}</span>
        </div>
        <div className="metric">
          <span className="metric-label">{t('impactAnalyzer.metrics.richDescriptions')}:</span>
          <span className="metric-value">{metrics.richDescriptionRate}%</span>
        </div>
        <div className="metric">
          <span className="metric-label">{t('impactAnalyzer.metrics.testCoverage')}:</span>
          <span className="metric-value">{metrics.testCoverageRate}%</span>
        </div>
      </div>
    </div>
  );
  
  const SmartTips = ({ tips = [] }) => (
    <div className="smart-tips">
      <h3><FaLightbulb /> {t('impactAnalyzer.smartTips')}</h3>
      {tips.map((tip, i) => (
        <div key={i} className="tip-item">
          {tip}
        </div>
      ))}
    </div>
  );
  
  return (
    <div className="impact-analyzer">
      <h2>{t('impactAnalyzer.title')}</h2>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
          <Spinner size="w-10 h-10" color="border-blue-500" />
        </div>
      )}

      <div className="impact-controls" style={loading ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
        <div className="control-group">
          <label>{t('impactAnalyzer.nodeLabel')}:</label>
          <select 
            value={selectedNode} 
            onChange={(e) => setSelectedNode(e.target.value)}
          >
            <option value="">{t('impactAnalyzer.selectNode')}</option>
            {nodes.map(node => (
              <option key={node.name} value={node.name}>{node.name}</option>
            ))}
          </select>
        </div>
        
        <div className="control-group">
          <label>{t('impactAnalyzer.changeTypeLabel')}:</label>
          <select
            value={changeType}
            onChange={(e) => setChangeType(e.target.value)}
          >
            <option value="update">{t('impactAnalyzer.changeTypes.update')}</option>
            <option value="delete">{t('impactAnalyzer.changeTypes.delete')}</option>
            <option value="add">{t('impactAnalyzer.changeTypes.add')}</option>
          </select>
        </div>
        
        <button 
          onClick={handleAnalyze}
          disabled={loading || !selectedNode}
          className="analyze-btn"
        >
          {loading ? t('impactAnalyzer.analyzing') : t('impactAnalyzer.analyzeButton')}
        </button>
      </div>
      
      {results && (
        <div className="impact-results">
          {/* Indicateur de confiance */}
          {results.confidenceScore !== undefined && (
            <ConfidenceIndicator score={results.confidenceScore} />
          )}
          
          <div className="impact-summary">
            <h3>{t('impactAnalyzer.impactSummary')}</h3>
            <div className="impact-score">
              <span className="score-label">{t('impactAnalyzer.riskScore')}:</span>
              <span className={`score-value ${
                results.riskScore > 50 ? "high-risk" : 
                results.riskScore > 25 ? "medium-risk" : "low-risk"
              }`}>
                {results.riskScore}
              </span>
            </div>
            <p className="impact-description">{results.impactSummary}</p>
          </div>
          
          {/* Métriques du graphe */}
          {results.graphMetrics && (
            <GraphMetrics metrics={results.graphMetrics} />
          )}
          
          {/* Actions contextualisées */}
          {results.contextualActions && results.contextualActions.length > 0 && (
            <ContextualActions actions={results.contextualActions} />
          )}
          
          <div className="impacted-nodes">
            <h3>{t('impactAnalyzer.impactedNodes', { count: impactedNodes.length })}</h3>
            {impactedNodes.map((node, i) => (
              <div key={i} className="impacted-node">
                <div className="node-header">
                  {node.impactType === "core-feature" && 
                    <FaExclamationTriangle className="impact-icon critical" />
                  }
                  {node.impactType === "test-coverage-missing" && 
                    <FaTasks className="impact-icon warning" />
                  }
                  {node.impactType === "direct" && 
                    <FaCheckCircle className="impact-icon info" />
                  }
                  <h4 className="node-name" onClick={() => onNodeSelect(node.name)}>
                    {node.name}
                  </h4>
                  {node.riskScore && (
                    <span className="node-risk">{t('impactAnalyzer.risk')}: {node.riskScore}</span>
                  )}
                </div>
                
                {node.riskExplanation && (
                  <div className="risk-explanation">
                    <strong>{t('impactAnalyzer.whyRisky')}:</strong> {node.riskExplanation}
                  </div>
                )}
                
                {node.suggestedAction && (
                  <div className="action-suggested">
                    <strong>{t('impactAnalyzer.suggestedAction')}:</strong> {node.suggestedAction}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Conseils intelligents */}
          {results.smartTips && results.smartTips.length > 0 && (
            <SmartTips tips={results.smartTips} />
          )}
          
          {results.planTests && results.planTests.length > 0 && (
            <div className="tests-to-generate">
              <h3>{t('impactAnalyzer.testsToGenerate', { count: results.planTests.length })}</h3>
              <ul>
                {results.planTests.map((test, i) => (
                  <li key={i} className="test-item">
                    <strong>{test.name || t('impactAnalyzer.testNumber', { number: i+1 })}</strong>
                    <div className="test-description">{test.description}</div>
                    {test.priority && (
                      <span className={`test-priority ${test.priority}`}>
                        {t('impactAnalyzer.priority')}: {test.priority}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Analytics globales */}
          {analytics && (
            <div className="impact-analytics">
              <h3>{t('impactAnalyzer.analysisStatistics')}</h3>
              <div className="analytics-grid">
                <div className="analytic-item">
                  <span>{t('impactAnalyzer.analytics.analysesThisWeek')}:</span>
                  <strong>{analytics.totalAnalyses}</strong>
                </div>
                <div className="analytic-item">
                  <span>{t('impactAnalyzer.analytics.averageRisk')}:</span>
                  <strong>{Math.round(analytics.avgRiskScore)}%</strong>
                </div>
                <div className="analytic-item">
                  <span>{t('impactAnalyzer.analytics.averageTime')}:</span>
                  <strong>{Math.round(analytics.avgAnalysisTime)}ms</strong>
                </div>
                <div className="analytic-item">
                  <span>{t('impactAnalyzer.analytics.cacheHitRate')}:</span>
                  <strong>{Math.round((analytics.cacheHitRate || 0) * 100)}%</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ImpactAnalyzer;